import { FormInstance, message } from 'antd';
import { ErrorCode } from 'constants/errors';
import { ErrorResponse } from 'libs/pinnacle-sdk';
import { FieldData } from 'rc-field-form/es/interface';
import * as yup from 'yup';

export const validateForm = async <T>(
  values: object,
  form: FormInstance<T>,
  fields: (keyof T)[],
  validationSchema: yup.Schema
) => {
  try {
    await validationSchema.validate(values, {
      abortEarly: false,
    });
    form.setFields(
      fields.map(
        (field) =>
          ({
            name: field,
            errors: [] as string[],
          }) as FieldData<T>
      )
    );
    return true;
  } catch (error) {
    if (error instanceof yup.ValidationError) {
      const validFields = fields.filter(
        (field) => !error.inner.some((err) => err.path === field)
      );
      const errorFields = error.inner.map((err) => ({
        name: err.path,
        errors: err.errors,
      }));
      form.setFields(errorFields as FieldData<T>[]);
      form.setFields(
        validFields.map(
          (field) =>
            ({
              name: field,
              errors: [] as string[],
            }) as FieldData<T>
        )
      );
    }
    return false;
  }
};

const isValidField = <T>(value: T) => {
  return value !== '';
};

export const sanitizeFormValue = <T extends object>(values: T): T => {
  return Object.entries(values).reduce(
    (acc, [key, value]) => ({
      ...acc,
      ...(isValidField(value) ? { [key]: value } : {}),
    }),
    {}
  ) as T;
};

export const handleError = async ({
  error,
  defaultMessage = 'An unknown error has occurred. Please try again later',
}: {
  error: { response: Response };
  defaultMessage?: string;
}) => {
  try {
    const errorObj: ErrorResponse = await error.response.json();
    let errorMessage = defaultMessage;
    if (errorObj.statusCode === ErrorCode.BAD_REQUEST && errorObj.message) {
      errorMessage = errorObj.message;
    }
    message.error(errorMessage);
  } catch {
    message.error(defaultMessage);
  }
};
